import { useState } from 'react';
import { Wallet, Clock, Utensils, User, Phone, MapPin, FileText, Users, Calendar, RefreshCw, Tag, Pencil, Percent, Banknote, Truck, Clock3, StickyNote, Trash2, AlertTriangle, AlertCircle } from 'lucide-react';
import { Modal, Button, StatusBadge } from '../common';
import { STATUS_META, COMPANY_SETTINGS } from '../../utils/constants';
import { touchInteraction } from '../../services/restaurantService';

const FULL_SIGNUP_FEE = 300;
const DISCOUNTED_SIGNUP_FEE = 150;

/**
 * View Restaurant Details Modal
 * Shows all restaurant information in a read-only view
 */
export function ViewRestaurantModal({ 
  isOpen, 
  onClose, 
  restaurant, 
  users,
  currentUser,
  showToast,
  onUpdateSuccess,
  settings,
  updateRestaurant,
  setModal
}) {
  if (!restaurant) return null;

  const [scheduledCallAt, setScheduledCallAt] = useState(restaurant.scheduled_call_at || '');
  const [paymentMethod, setPaymentMethod] = useState(restaurant.paymentMethod || 'immediate');
  const [updatingPayment, setUpdatingPayment] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [showWrongInfoModal, setShowWrongInfoModal] = useState(false);
  const [wrongInfoType, setWrongInfoType] = useState('');
  const [wrongInfoDescription, setWrongInfoDescription] = useState('');
  const [verificationChecks, setVerificationChecks] = useState({
    googleSearch: false,
    nameSearch: false,
    calledNumber: false,
    visitedLocation: false,
  });
  const [showDetails, setShowDetails] = useState(false);

  // Reset registration info (Admin only)
  const handleResetRegistration = async () => {
    setResetting(true);
    try {
      await updateRestaurant(restaurant.id, {
        // Clear all registration fields
        ownerName: null,
        ownerPhone: null,
        whatsappNumber: null,
        taxNumber: null,
        commissionPercentage: null,
        signupFee: null,
        paymentMethod: null,
        deliveryRadiusKm: null,
        defaultDeliveryFee: null,
        minimumOrderAmount: null,
        discountOfferFirst5: null,
        openTime: null,
        closeTime: null,
        visitDate: null,
        visitTime: null,
        visitNotes: null,
        notes: null,
        // Reset status back to new
        status: 'new',
      });
      showToast('✅ تم إعادة تعيين بيانات المطعم بنجاح');
      setShowResetConfirm(false);
      onUpdateSuccess?.();
      onClose();
    } catch (err) {
      console.error('Reset registration error:', err);
      showToast('❌ فشل في إعادة التعيين', 'error');
    } finally {
      setResetting(false);
    }
  };

  // Flag contact as having wrong info
  const handleFlagWrongInfo = async () => {
    if (!wrongInfoType) {
      showToast('⚠️ يرجى اختيار نوع الخطأ', 'warning');
      return;
    }

    // Mandatory: Must have searched restaurant name on Google
    if (!verificationChecks.nameSearch) {
      showToast('⚠️ يجب البحث عن اسم المطعم عبر Google قبل الإبلاغ', 'warning');
      return;
    }

    try {
      await updateRestaurant(restaurant.id, {
        wrongInfoFlag: true,
        wrongInfoType: wrongInfoType,
        wrongInfoDescription: wrongInfoDescription || null,
        wrongInfoFlaggedBy: currentUser.id,
        wrongInfoFlaggedAt: new Date().toISOString(),
        wrongInfoStatus: 'pending',
        wrongInfoVerification: verificationChecks,
      });
      showToast('✅ تم إرسال البلاغ للإدارة');
      setShowWrongInfoModal(false);
      setWrongInfoType('');
      setWrongInfoDescription('');
      setVerificationChecks({
        googleSearch: false,
        nameSearch: false,
        calledNumber: false,
        visitedLocation: false,
      });
      onUpdateSuccess?.();
      onClose();
    } catch (err) {
      console.error('Wrong info flag error:', err);
      showToast('❌ فشل في إرسال البلاغ', 'error');
    }
  };

  // Fee derived from payment method
  const effectiveFee = paymentMethod === 'immediate' ? DISCOUNTED_SIGNUP_FEE : FULL_SIGNUP_FEE;

  const marketer = users?.find(u => u.id === restaurant.assignedTo);
  const rep = users?.find(u => u.id === restaurant.assignedRep);
  const statusMeta = STATUS_META[restaurant.status] || { label: restaurant.status, color: '#64748b', icon: 'ClipboardList' };

  // Check if ANY registration data exists
  const hasRegistrationData = !!(
    restaurant.ownerName || 
    restaurant.ownerPhone || 
    restaurant.whatsappNumber || 
    restaurant.taxNumber || 
    restaurant.commissionPercentage || 
    restaurant.signupFee || 
    restaurant.deliveryRadiusKm || 
    restaurant.defaultDeliveryFee || 
    restaurant.minimumOrderAmount || 
    restaurant.discountOfferFirst5 ||
    restaurant.openTime || 
    restaurant.closeTime || 
    restaurant.visitDate || 
    restaurant.visitTime || 
    restaurant.visitNotes || 
    restaurant.notes
  );

  const InfoRow = ({ label, value, icon }) => (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'flex-start',
      padding: '12px 0',
      borderBottom: '1px solid #e2e8f0',
    }}>
      <span style={{ color: '#64748b', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
        {icon} {label}
      </span>
      <span style={{ color: '#000000', fontSize: 13, fontWeight: 600, textAlign: 'left', maxWidth: '60%' }}>
        {value || '—'}
      </span>
    </div>
  );

  return (
    <Modal 
      title={`🍽️ ${restaurant.name}`} 
      isOpen={isOpen} 
      onClose={onClose}
      size="lg"
    >
      <div style={{ direction: 'rtl' }}>
        {/* Status Badge */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          marginBottom: 24,
          padding: 16,
          background: statusMeta.bg || '#f1f5f9',
          borderRadius: 12,
          border: `1px solid ${statusMeta.color}44`,
        }}>
          <StatusBadge status={restaurant.status} size="lg" />
        </div>

        {/* Claim Status Banner */}
        {restaurant.claimedBy && (
          <div style={{
            marginBottom: 16,
            padding: 14,
            background: restaurant.claimedBy === currentUser.id 
              ? 'linear-gradient(135deg, #fef2f2, #fee2e2)' 
              : 'linear-gradient(135deg, #f5f3ff, #ede9fe)',
            borderRadius: 10,
            border: restaurant.claimedBy === currentUser.id ? '1px solid #f8717144' : '1px solid #a78bfa44',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}>
            <span style={{ fontSize: 20 }}>
              {restaurant.claimedBy === currentUser.id ? '🔒' : '🔐'}
            </span>
            <div style={{ flex: 1 }}>
              {restaurant.claimedBy === currentUser.id ? (
                <>
                  <span style={{ color: '#f87171', fontSize: 13, fontWeight: 700 }}>
                    محجوز بواسطتك
                  </span>
                  {restaurant.claimedAt && (
                    <span style={{ color: '#000000', fontSize: 11, marginRight: 8 }}>
                      ⏳ متبقي {(() => {
                        const claimedAt = new Date(restaurant.claimedAt);
                        const expiresAt = new Date(claimedAt.getTime() + 48 * 60 * 60 * 1000);
                        const remaining = expiresAt - new Date();
                        if (remaining <= 0) return 'منتهي';
                        const hours = Math.floor(remaining / (1000 * 60 * 60));
                        const mins = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
                        return hours > 0 ? `${hours}س ${mins}د` : `${mins}د`;
                      })()}
                    </span>
                  )}
                </>
              ) : (
                <span style={{ color: '#a78bfa', fontSize: 13, fontWeight: 700 }}>
                  محجوز بواسطة {users?.find(u => u.id === restaurant.claimedBy)?.name || 'مستخدم آخر'}
                </span>
              )}
            </div>
            {restaurant.claimedBy === currentUser.id && (
              <button
                onClick={async () => {
                  const { releaseClaim } = await import('../../services/restaurantService');
                  await releaseClaim(restaurant.id);
                  showToast('✅ تم تحرير المطعم');
                  onUpdateSuccess?.();
                  onClose();
                }}
                style={{
                  padding: '6px 12px',
                  background: '#ef444422',
                  border: '1px solid #ef4444',
                  borderRadius: 6,
                  color: '#f87171',
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontFamily: "'Cairo', sans-serif",
                }}
              >
                🔓 تحرير
              </button>
            )}
          </div>
        )}

        {/* Wrong Info Status Banner */}
        {restaurant.wrongInfoFlag && (
          <div style={{
            marginBottom: 24,
            padding: 20,
            background: restaurant.wrongInfoStatus === 'pending' 
              ? 'linear-gradient(135deg, #fff7ed, #ffedd5)' 
              : restaurant.wrongInfoStatus === 'returned'
                ? 'linear-gradient(135deg, #eff6ff, #dbeafe)'
                : 'linear-gradient(135deg, #f0fdfa, #d1fae5)',
            borderRadius: 12,
            border: restaurant.wrongInfoStatus === 'pending' 
              ? '2px solid #f59e0b' 
              : restaurant.wrongInfoStatus === 'returned'
                ? '2px solid #4ecdc4'
                : '2px solid #10b981',
          }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: restaurant.wrongInfoStatus === 'pending' ? '#f59e0b22' : restaurant.wrongInfoStatus === 'returned' ? '#4ecdc422' : '#10b98122',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <AlertCircle size={22} color={restaurant.wrongInfoStatus === 'pending' ? '#f59e0b' : restaurant.wrongInfoStatus === 'returned' ? '#4ecdc4' : '#10b981'} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ 
                  color: restaurant.wrongInfoStatus === 'pending' ? '#fcd34d' : restaurant.wrongInfoStatus === 'returned' ? '#60a5fa' : '#34d399',
                  fontSize: 16, fontWeight: 900,
                }}>
                  {restaurant.wrongInfoStatus === 'pending' ? '⏳ بانتظار مراجعة الإدارة' 
                    : restaurant.wrongInfoStatus === 'returned' ? '🔄 تم الإعادة للتصحيح' 
                    : '✅ تم التصحيح'}
                </div>
                <div style={{ color: '#000000', fontSize: 12, marginTop: 2 }}>
                  {restaurant.wrongInfoType === 'wrong_number' ? 'رقم هاتف خاطئ' 
                    : restaurant.wrongInfoType === 'wrong_name' ? 'اسم المطعم خاطئ' 
                    : restaurant.wrongInfoType === 'wrong_location' ? 'موقع خاطئ' 
                    : restaurant.wrongInfoType === 'duplicate' ? 'مكرر' 
                    : restaurant.wrongInfoType === 'closed' ? 'المطعم مغلق' 
                    : 'أخرى'}
                  {restaurant.wrongInfoDescription && ` — ${restaurant.wrongInfoDescription}`}
                </div>
              </div>
            </div>

            {/* Admin Note */}
            {restaurant.wrongInfoAdminNote && (
              <div style={{
                padding: 12,
                background: '#ffffff',
                borderRadius: 8,
                borderRight: `3px solid ${restaurant.wrongInfoStatus === 'returned' ? '#4ecdc4' : '#10b981'}`,
                marginBottom: 12,
              }}>
                <div style={{ color: '#60a5fa', fontSize: 11, fontWeight: 700, marginBottom: 4 }}>
                  💬 ملاحظة الإدارة:
                </div>
                <div style={{ color: '#000000', fontSize: 13 }}>
                  {restaurant.wrongInfoAdminNote}
                </div>
              </div>
            )}

            {/* Marketer: Acknowledge and Continue */}
            {currentUser?.role === 'marketer' && (restaurant.wrongInfoStatus === 'returned' || restaurant.wrongInfoStatus === 'corrected') && (
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={async () => {
                    try {
                      await updateRestaurant(restaurant.id, {
                        wrongInfoFlag: false,
                        wrongInfoStatus: 'resolved',
                      });
                      showToast('✅ تم تأكيد الاستلام، يمكنك متابعة العمل');
                      onUpdateSuccess?.();
                      onClose();
                    } catch (err) {
                      console.error('Acknowledge error:', err);
                      showToast('❌ فشل في التأكيد', 'error');
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: '10px 16px',
                    background: restaurant.wrongInfoStatus === 'corrected' 
                      ? 'linear-gradient(135deg, #10b981, #059669)' 
                      : 'linear-gradient(135deg, #4ecdc4, #38b2ac)',
                    border: 'none',
                    borderRadius: 8,
                    color: '#fff',
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: 'pointer',
                    fontFamily: "'Cairo', sans-serif",
                  }}
                >
                  ✅ تأكيد الاستلام ومتابعة العمل
                </button>
              </div>
            )}

            {/* Marketer: Pending status message */}
            {currentUser?.role === 'marketer' && restaurant.wrongInfoStatus === 'pending' && (
              <div style={{
                padding: 10,
                background: '#f59e0b11',
                borderRadius: 8,
                border: '1px solid #f59e0b44',
              }}>
                <p style={{ color: '#fcd34d', fontSize: 12, margin: 0 }}>
                  ⏳ تم إرسال البلاغ للإدارة وهو قيد المراجعة. سيتم إعلامك بالنتيجة.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Restaurant Logo */}
        {restaurant.files?.logoUrl && (
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <img 
              src={restaurant.files.logoUrl} 
              alt={restaurant.name}
              style={{ 
                width: 80, 
                height: 80, 
                borderRadius: 16, 
                objectFit: 'cover',
                border: '2px solid #e2e8f0',
              }} 
              onError={e => { e.target.style.display = 'none'; }}
            />
          </div>
        )}

        {/* Basic Information - SIMPLIFIED */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ 
            padding: 20, 
            background: '#f8fafc', 
            borderRadius: 12,
            border: '1px solid #e2e8f0',
          }}>
            {/* Restaurant Name & Status */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ color: '#000000', fontSize: 20, fontWeight: 900, margin: 0 }}>
                {restaurant.name}
              </h2>
              <StatusBadge status={restaurant.status} />
            </div>

            {/* Contact Info */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {restaurant.phone && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 18 }}>📱</span>
                  <div style={{ flex: 1 }}>
                    <a href={`tel:${restaurant.phone}`} style={{ color: '#4ecdc4', textDecoration: 'none', fontWeight: 700, fontSize: 16 }}>
                      {restaurant.phone}
                    </a>
                  </div>
                  <a 
                    href={`https://wa.me/${restaurant.phone.replace(/\D/g, '')}?text=${encodeURIComponent(settings?.whatsappMsg || COMPANY_SETTINGS.whatsappMsg || '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ 
                      padding: '8px 16px', 
                      background: '#25D366', 
                      color: '#fff', 
                      borderRadius: 8, 
                      fontSize: 13,
                      fontWeight: 700,
                      textDecoration: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    واتساب
                  </a>
                </div>
              )}
              
              {restaurant.area && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 18 }}>📍</span>
                  <span style={{ color: '#000000', fontSize: 14, fontWeight: 600 }}>{restaurant.area}</span>
                </div>
              )}

              {restaurant.mapsUrl && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 18 }}>🗺️</span>
                  <a href={restaurant.mapsUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#4ecdc4', textDecoration: 'none', fontWeight: 600, fontSize: 14 }}>
                    فتح في Google Maps
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions - REORGANIZED */}
        <div style={{ 
          marginBottom: 24, 
          padding: 20,
          background: '#ffffff',
          borderRadius: 12,
          border: '1px solid #e2e8f0',
        }}>
          <h3 style={{ color: '#000000', fontSize: 16, fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 18 }}>⚡</span>
            إجراءات سريعة
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {/* Primary Action - Continue/Start Registration */}
            <button
              onClick={() => {
                touchInteraction(restaurant.id);
                onClose();
                setModal({ type: 'onboarding', data: restaurant });
              }}
              style={{ 
                gridColumn: '1 / -1',
                padding: '14px 20px',
                background: hasRegistrationData 
                  ? 'linear-gradient(135deg, #4ecdc4 0%, #38b2ac 100%)' 
                  : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                border: 'none',
                borderRadius: 10,
                color: '#ffffff',
                fontSize: 15,
                fontWeight: 700,
                cursor: 'pointer',
                fontFamily: "'Cairo', sans-serif",
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                boxShadow: '0 2px 8px rgba(78, 205, 196, 0.3)',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
            >
              {hasRegistrationData ? (
                <>
                  <span style={{ fontSize: 18 }}>✏️</span>
                  متابعة التعديل
                </>
              ) : (
                <>
                  <span style={{ fontSize: 18 }}>🚀</span>
                  بدء التسجيل
                </>
              )}
            </button>

            {/* Call Button */}
            {restaurant.phone && (
              <a
                href={`tel:${restaurant.phone}`}
                style={{
                  padding: '12px 16px',
                  background: '#f0fdfa',
                  border: '1px solid #4ecdc4',
                  borderRadius: 8,
                  color: '#4ecdc4',
                  fontSize: 13,
                  fontWeight: 700,
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  transition: 'all 0.2s ease',
                }}
              >
                📞 اتصال
              </a>
            )}

            {/* WhatsApp Button */}
            {restaurant.phone && (
              <a
                href={`https://wa.me/${restaurant.phone.replace(/\D/g, '')}?text=${encodeURIComponent(settings?.whatsappMsg || COMPANY_SETTINGS.whatsappMsg || '')}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  padding: '12px 16px',
                  background: '#f0fdf4',
                  border: '1px solid #25D366',
                  borderRadius: 8,
                  color: '#25D366',
                  fontSize: 13,
                  fontWeight: 700,
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  transition: 'all 0.2s ease',
                }}
              >
                واتساب
              </a>
            )}

            {/* Schedule Call Card */}
            <div style={{
              gridColumn: '1 / -1',
              padding: 12,
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 10,
            }}>
              <label style={{ color: '#000000', fontSize: 12, fontWeight: 600, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ fontSize: 14 }}>📅</span>
                جدولة مكالمة أخرى
              </label>
              <input
                type="datetime-local"
                value={scheduledCallAt || ''}
                onChange={(e) => setScheduledCallAt(e.target.value)}
                style={{
                  width: '100%',
                  background: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  padding: '8px 10px',
                  color: '#000000',
                  fontSize: 13,
                  fontFamily: "'Cairo', sans-serif",
                  marginBottom: 8,
                  boxSizing: 'border-box',
                }}
              />
              <button 
                onClick={async () => {
                  if (!scheduledCallAt) {
                    showToast('يرجى اختيار التاريخ والوقت', 'error');
                    return;
                  }
                  await updateRestaurant(restaurant.id, {
                    scheduled_call_at: new Date(scheduledCallAt).toISOString(),
                  });
                  touchInteraction(restaurant.id);
                  showToast('✅ تم جدولة المكالمة بنجاح');
                  onUpdateSuccess?.();
                }}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  background: '#4ecdc4',
                  border: 'none',
                  borderRadius: 8,
                  color: '#ffffff',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: "'Cairo', sans-serif",
                }}
              >
                حفظ موعد المكالمة
              </button>
            </div>

            {/* Not Interested */}
            <button
              onClick={async () => {
                const reason = prompt('لماذا المطعم غير مهتم؟ (اختياري)');
                if (reason !== null) {
                  await updateRestaurant(restaurant.id, {
                    notes: restaurant.notes 
                      ? `${restaurant.notes}\n\nغير مهتم: ${reason}`
                      : `غير مهتم: ${reason}`,
                  });
                  showToast('✅ تم تسجيل الملاحظة');
                  touchInteraction(restaurant.id);
                  onUpdateSuccess?.();
                  onClose();
                }
              }}
              style={{
                padding: '12px 16px',
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: 8,
                color: '#ef4444',
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
                fontFamily: "'Cairo', sans-serif",
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
              }}
            >
              ❌ غير مهتم
            </button>

            {/* Wrong Info */}
            {!restaurant.wrongInfoFlag && (
              <button
                onClick={() => setShowWrongInfoModal(true)}
                style={{
                  padding: '12px 16px',
                  background: '#fef3c7',
                  border: '1px solid #fde68a',
                  borderRadius: 8,
                  color: '#f59e0b',
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontFamily: "'Cairo', sans-serif",
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                }}
              >
                ⚠️ معلومات خاطئة
              </button>
            )}
          </div>
        </div>

        {/* Expand Details Button */}
        {(restaurant.ownerName || restaurant.ownerPhone || restaurant.whatsappNumber || restaurant.taxNumber || 
          restaurant.commissionPercentage || restaurant.signupFee || restaurant.deliveryRadiusKm || 
          restaurant.defaultDeliveryFee || restaurant.minimumOrderAmount || restaurant.discountOfferFirst5 ||
          restaurant.openTime || restaurant.closeTime || restaurant.visitDate || restaurant.visitTime || 
          restaurant.visitNotes || restaurant.notes) && (
          <div style={{ marginBottom: 24, textAlign: 'center' }}>
            <button
              onClick={() => setShowDetails(!showDetails)}
              style={{
                padding: '12px 24px',
                background: '#f8fafc',
                border: '2px solid #4ecdc4',
                borderRadius: 10,
                color: '#4ecdc4',
                fontSize: 14,
                fontWeight: 700,
                cursor: 'pointer',
                fontFamily: "'Cairo', sans-serif",
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                transition: 'all 0.2s ease',
              }}
            >
              {showDetails ? '🔼 إخفاء التفاصيل' : '🔽 عرض التفاصيل'}
            </button>
          </div>
        )}

        {/* Registration Information - Show if expanded and any data exists */}
        {showDetails && (restaurant.ownerName || restaurant.ownerPhone || restaurant.whatsappNumber || restaurant.taxNumber || 
          restaurant.commissionPercentage || restaurant.signupFee || restaurant.deliveryRadiusKm || 
          restaurant.defaultDeliveryFee || restaurant.minimumOrderAmount || restaurant.discountOfferFirst5 ||
          restaurant.openTime || restaurant.closeTime || restaurant.visitDate || restaurant.visitTime || 
          restaurant.visitNotes || restaurant.notes) && (
          <>
            {/* Reset Button for Admin */}
            {currentUser?.role === 'admin' && (
              <div style={{ marginBottom: 16 }}>
                <Button 
                  onClick={() => setShowResetConfirm(true)}
                  style={{ 
                    background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                    justifyContent: 'center',
                    width: '100%',
                  }}
                >
                  <Trash2 size={16} /> إعادة تعيين بيانات المطعم
                </Button>
              </div>
            )}

            {/* Owner Information */}
            {(restaurant.ownerName || restaurant.ownerPhone || restaurant.whatsappNumber || restaurant.taxNumber) && (
              <div style={{ marginBottom: 24 }}>
                <h3 style={{ color: '#000000', fontSize: 16, fontWeight: 700, marginBottom: 16 }}>
                  <User size={18} style={{ verticalAlign: 'middle', marginLeft: 8 }} />
                  معلومات المالك
                </h3>
                <InfoRow label="اسم المالك" value={restaurant.ownerName} icon={<User size={14} />} />
                <InfoRow label="رقم هاتف المالك" value={
                  restaurant.ownerPhone ? (
                    <a href={`tel:${restaurant.ownerPhone}`} style={{ color: '#4ecdc4', textDecoration: 'underline' }}>
                      {restaurant.ownerPhone}
                    </a>
                  ) : null
                } icon={<Phone size={14} />} />
                <InfoRow label="رقم الواتساب" value={
                  restaurant.whatsappNumber ? (
                    <a href={`https://wa.me/${restaurant.whatsappNumber.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" style={{ color: '#25D366', textDecoration: 'underline' }}>
                      {restaurant.whatsappNumber}
                    </a>
                  ) : null
                } icon={<Phone size={14} />} />
                <InfoRow label="الرقم الضريبي" value={restaurant.taxNumber} icon={<FileText size={14} />} />
              </div>
            )}

            {/* Financial Details */}
            {(restaurant.commissionPercentage || restaurant.signupFee || restaurant.discountOfferFirst5) && (
              <div style={{ marginBottom: 24 }}>
                <h3 style={{ color: '#000000', fontSize: 16, fontWeight: 700, marginBottom: 16 }}>
                  <Banknote size={18} style={{ verticalAlign: 'middle', marginLeft: 8 }} />
                  التفاصيل المالية
                </h3>
                <InfoRow label="نسبة العمولة" value={restaurant.commissionPercentage ? `${restaurant.commissionPercentage}%` : null} icon={<Percent size={14} />} />
                <InfoRow label="رسوم التسجيل" value={restaurant.signupFee ? `${restaurant.signupFee} AED` : null} icon={<Tag size={14} />} />
                <InfoRow label="طريقة الدفع" value={
                  restaurant.paymentMethod === 'pay_on_topup' ? 'الدفع بعد الشحن الأول' : 'دفع فوري'
                } icon={<Wallet size={14} />} />
                <InfoRow label="خصم أول 5 طلبات" value={restaurant.discountOfferFirst5} icon={<Tag size={14} />} />
              </div>
            )}

            {/* Delivery Settings */}
            {(restaurant.deliveryRadiusKm || restaurant.defaultDeliveryFee || restaurant.minimumOrderAmount) && (
              <div style={{ marginBottom: 24 }}>
                <h3 style={{ color: '#000000', fontSize: 16, fontWeight: 700, marginBottom: 16 }}>
                  <Truck size={18} style={{ verticalAlign: 'middle', marginLeft: 8 }} />
                  إعدادات التوصيل
                </h3>
                <InfoRow label="نطاق التوصيل" value={restaurant.deliveryRadiusKm ? `${restaurant.deliveryRadiusKm} كم` : null} icon={<MapPin size={14} />} />
                <InfoRow label="رسوم التوصيل" value={restaurant.defaultDeliveryFee ? `${restaurant.defaultDeliveryFee} AED` : null} icon={<Banknote size={14} />} />
                <InfoRow label="الحد الأدنى للطلب" value={restaurant.minimumOrderAmount ? `${restaurant.minimumOrderAmount} AED` : null} icon={<Banknote size={14} />} />
              </div>
            )}

            {/* Operating Hours */}
            {(restaurant.openTime || restaurant.closeTime) && (
              <div style={{ marginBottom: 24 }}>
                <h3 style={{ color: '#000000', fontSize: 16, fontWeight: 700, marginBottom: 16 }}>
                  <Clock3 size={18} style={{ verticalAlign: 'middle', marginLeft: 8 }} />
                  أوقات العمل
                </h3>
                <InfoRow label="وقت الافتتاح" value={restaurant.openTime} icon={<Clock size={14} />} />
                <InfoRow label="وقت الإغلاق" value={restaurant.closeTime} icon={<Clock size={14} />} />
              </div>
            )}

            {/* Visit Scheduling */}
            {(restaurant.visitDate || restaurant.visitTime || restaurant.visitNotes) && (
              <div style={{ marginBottom: 24 }}>
                <h3 style={{ color: '#000000', fontSize: 16, fontWeight: 700, marginBottom: 16 }}>
                  <Calendar size={18} style={{ verticalAlign: 'middle', marginLeft: 8 }} />
                  جدولة الزيارة
                </h3>
                <InfoRow label="تاريخ الزيارة" value={
                  restaurant.visitDate ? new Date(restaurant.visitDate).toLocaleDateString('ar-AE') : null
                } icon={<Calendar size={14} />} />
                <InfoRow label="وقت الزيارة" value={restaurant.visitTime} icon={<Clock size={14} />} />
                {restaurant.visitNotes && (
                  <div style={{ 
                    padding: 12, 
                    background: '#ffffff', 
                    borderRadius: 8, 
                    border: '1px solid #e2e8f0',
                    marginTop: 8,
                  }}>
                    <div style={{ color: '#000000', fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
                      ملاحظات الزيارة:
                    </div>
                    <div style={{ color: '#000000', fontSize: 13 }}>
                      {restaurant.visitNotes}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* General Notes */}
            {restaurant.notes && (
              <div style={{ marginBottom: 24 }}>
                <h3 style={{ color: '#000000', fontSize: 16, fontWeight: 700, marginBottom: 16 }}>
                  <StickyNote size={18} style={{ verticalAlign: 'middle', marginLeft: 8 }} />
                  ملاحظات
                </h3>
                <div style={{ 
                  padding: 12, 
                  background: '#ffffff', 
                  borderRadius: 8, 
                  border: '1px solid #e2e8f0',
                }}>
                  <div style={{ color: '#000000', fontSize: 13, whiteSpace: 'pre-wrap' }}>
                    {restaurant.notes}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Files & Documents */}
        {restaurant.files && Object.values(restaurant.files).some(Boolean) && (
          <div style={{ marginBottom: 24 }}>
            <h3 style={{ color: '#000000', fontSize: 16, fontWeight: 700, marginBottom: 16 }}>
              📎 الملفات والمستندات
            </h3>
            {restaurant.files.logoUrl && (
              <InfoRow 
                label="الشعار" 
                value={
                  <a href={restaurant.files.logoUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#4ecdc4', textDecoration: 'underline' }}>
                    عرض الشعار
                  </a>
                } 
                icon="🖼️" 
              />
            )}
            {restaurant.files.menu1Url && (
              <InfoRow 
                label="القائمة 1" 
                value={
                  <a href={restaurant.files.menu1Url} target="_blank" rel="noopener noreferrer" style={{ color: '#4ecdc4', textDecoration: 'underline' }}>
                    عرض القائمة
                  </a>
                } 
                icon="📄" 
              />
            )}
            {restaurant.files.menu2Url && (
              <InfoRow 
                label="القائمة 2" 
                value={
                  <a href={restaurant.files.menu2Url} target="_blank" rel="noopener noreferrer" style={{ color: '#4ecdc4', textDecoration: 'underline' }}>
                    عرض القائمة
                  </a>
                } 
                icon="📄" 
              />
            )}
            {restaurant.files.tradeLicenseUrl && (
              <InfoRow 
                label="الرخصة التجارية" 
                value={
                  <a href={restaurant.files.tradeLicenseUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#4ecdc4', textDecoration: 'underline' }}>
                    عرض الرخصة
                  </a>
                } 
                icon="📜" 
              />
            )}
            {restaurant.files.ownerIdUrl && (
              <InfoRow 
                label="هوية المالك" 
                value={
                  <a href={restaurant.files.ownerIdUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#4ecdc4', textDecoration: 'underline' }}>
                    عرض الهوية
                  </a>
                } 
                icon="🆔" 
              />
            )}
            {restaurant.files.invoiceUrl && (
              <InfoRow 
                label="الفاتورة" 
                value={
                  <a href={restaurant.files.invoiceUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#4ecdc4', textDecoration: 'underline' }}>
                    عرض الفاتورة
                  </a>
                } 
                icon="💰" 
              />
            )}
          </div>
        )}

        {/* Return Note */}
        {restaurant.returnNote && (
          <div style={{ marginBottom: 24 }}>
            <div style={{ 
              padding: 12, 
              background: '#ffffff', 
              borderRadius: 8, 
              border: '1px solid #f97316',
            }}>
              <div style={{ color: '#f97316', fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
                ⚠️ ملاحظة الإرجاع:
              </div>
              <div style={{ color: '#000000', fontSize: 13 }}>
                {restaurant.returnNote}
              </div>
            </div>
          </div>
        )}

        {/* Created/Updated Dates */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          padding: '12px 0',
          borderTop: '1px solid #e2e8f0',
          fontSize: 11,
          color: '#64748b',
        }}>
          <span>
            📅 تاريخ الإنشاء: {restaurant.created_at ? new Date(restaurant.created_at).toLocaleDateString('ar-AE') : '—'}
          </span>
          <span>
            🔄 آخر تحديث: {restaurant.updated_at ? new Date(restaurant.updated_at).toLocaleDateString('ar-AE') : '—'}
          </span>
        </div>

        {/* Scheduled Call Display */}
        {restaurant.scheduled_call_at && (
          <div style={{
            marginTop: 16,
            padding: 12,
            background: new Date(restaurant.scheduled_call_at) > new Date() ? '#3a2800' : '#ffffff',
            borderRadius: 8,
            border: `1px solid ${new Date(restaurant.scheduled_call_at) > new Date() ? '#f59e0b' : '#10b981'}`,
          }}>
            <div style={{ color: new Date(restaurant.scheduled_call_at) > new Date() ? '#f59e0b' : '#10b981', fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
              📅 {new Date(restaurant.scheduled_call_at) > new Date() ? 'مكالمة مجدولة' : 'تم الاتصال'}
            </div>
            <div style={{ color: '#000000', fontSize: 13 }}>
              {new Date(restaurant.scheduled_call_at).toLocaleString('ar-AE', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </div>
          </div>
        )}

      </div>
    </Modal>
  );
}
